import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import SecureImage from './SecureImage';

// Module-level cache to store fetched videos by URL
// This prevents duplicate API calls when components re-render or same video appears in multiple places
// Exported so ShareSocialButton can reuse cached videos during share
export const videoCache = new Map();

// Module-level pending requests tracker to prevent duplicate fetches
const pendingRequests = new Map();
const posterCache = new Map(); // Add poster cache

// Limit cache size for videos (they're larger than images); keep generous for 4K sessions
const MAX_VIDEO_CACHE_SIZE = 24;
const MAX_POSTER_CACHE_SIZE = 20;

const cleanupPosterCache = () => {
  if (posterCache.size > MAX_POSTER_CACHE_SIZE) {
    const keysToDelete = Array.from(posterCache.keys()).slice(0, Math.floor(MAX_POSTER_CACHE_SIZE * 0.2));
    keysToDelete.forEach(key => {
      const url = posterCache.get(key);
      if (url) URL.revokeObjectURL(url);
      posterCache.delete(key);
    });
  }
};

const cleanupVideoCache = () => {
  if (videoCache.size > MAX_VIDEO_CACHE_SIZE) {
    // Remove oldest entries (first 20%) from the lookup map only.
    // Do NOT call URL.revokeObjectURL here: a <video> may still be playing that blob (Firefox
    // especially aborts 4K playback with "media resource aborted" if the object URL is revoked
    // while decode/buffer is active; Chrome often tolerates it).
    const keysToDelete = Array.from(videoCache.keys()).slice(0, Math.floor(MAX_VIDEO_CACHE_SIZE * 0.2));
    keysToDelete.forEach((key) => {
      videoCache.delete(key);
    });
  }
};

/**
 * SecureVideo Component
 * 
 * This component fetches videos with proper JWT authentication headers
 * instead of using regular <video> tags which cannot send headers.
 * 
 * @param {Object} props
 * @param {string} props.src - The video URL to fetch
 * @param {Object} props.style - CSS styles to apply
 * @param {Function} props.onLoad - Callback when video loads
 * @param {Function} props.onError - Callback when video fails to load
 * @param {string} props.className - CSS class names
 * @param {Object} props.otherProps - Any other props to pass through
 */
const SecureVideo = ({
  src,
  poster,
  style = {},
  onLoad,
  onError,
  className,
  controls = false, // Add controls prop with default false
  ...otherProps
}) => {
  // Initialize from cache if available to prevent flicker
  const initialVideoData = src && videoCache.has(src) ? videoCache.get(src) : null;
  const initialLoading = !initialVideoData;

  const [videoData, setVideoData] = useState(initialVideoData);
  const [posterData, setPosterData] = useState(poster && posterCache.has(poster) ? posterCache.get(poster) : null);
  const [posterLoaded, setPosterLoaded] = useState(false); // Track when poster image loads
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(false);
  const location = useLocation();

  // Use refs to store callbacks to avoid re-fetching on every render
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  const mountedRef = useRef(true);

  // Detect if we're in admin portal context
  const isAdminPortal = location.pathname.startsWith('/glam-admin/');

  // Update refs when callbacks change
  useEffect(() => {
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
  }, [onLoad, onError]);

  useEffect(() => {
    mountedRef.current = true;

    if (!src) {
      setError(true);
      setLoading(false);
      return () => {
        mountedRef.current = false;
      };
    }

    // Avoid showing the previous clip at opacity 0 while the new URL loads (felt like "play is stuck")
    setPosterLoaded(false);
    if (videoCache.has(src)) {
      setVideoData(videoCache.get(src));
    } else {
      setVideoData(null);
    }

    const fetchVideo = async () => {
      try {
        // 1. Check completed cache first
        if (videoCache.has(src)) {
          const cachedUrl = videoCache.get(src);
          setVideoData(cachedUrl);
          setLoading(false);
          setError(false);
          if (onLoadRef.current) {
            onLoadRef.current({ target: { src: cachedUrl } });
          }
          return;
        }

        // 2. Check if a fetch is already in progress for this URL
        // If so, reuse that promise instead of starting a new one
        if (pendingRequests.has(src)) {
          const resolvedUrl = await pendingRequests.get(src);
          if (!mountedRef.current || !resolvedUrl) return;
          setVideoData(resolvedUrl);
          setLoading(false);
          setError(false);
          if (onLoadRef.current) {
            onLoadRef.current({ target: { src: resolvedUrl } });
          }
          return;
        }

        // Set loading state; videoData already cleared above when this URL was not cached
        setLoading(true);
        setError(false);

        // Create the fetch promise
        const fetchPromise = (async () => {
          // Check if this is an S3 URL (pre-signed URLs don't need authentication)
          if (src.includes('amazonaws.com') || src.includes('s3.amazonaws.com')) {
            // This is an S3 URL - load directly without authentication
            const response = await fetch(src);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const blob = await response.blob();
            return URL.createObjectURL(blob);
          } else {
            // This is a backend endpoint - use authentication based on context
            let token;

            if (isAdminPortal) {
              // In admin portal, use admin token to access any merchant's data
              // Check localStorage first, then cookies as fallback for backward compatibility
              token = localStorage.getItem('access_token') || Cookies.get('access_token');
              if (!token) {
                throw new Error('No admin authentication token found');
              }
            } else {
              // In merchant portal, use merchant token
              token = Cookies.get('merchant_access_token');
              if (!token) {
                throw new Error('No merchant authentication token found');
              }
            }

            // Use backend stream URL with token; <video> follows the 307 redirect to S3 natively.
            // Do NOT fetch() the presigned S3 URL from JS — that triggers CORS preflight and fails
            // unless the bucket allows your app origin (not required for <video> playback).
            const sep = src.includes('?') ? '&' : '?';
            return `${src}${sep}token=${encodeURIComponent(token)}`;
          }
        })();

        // Store promise in pendingRequests
        pendingRequests.set(src, fetchPromise);

        // Await the promise
        const objectUrl = await fetchPromise;
        if (!mountedRef.current || !objectUrl) return;

        // Only cache blob URLs (S3); tokenized stream URLs expire with the JWT
        if (typeof objectUrl === 'string' && objectUrl.startsWith('blob:')) {
          videoCache.set(src, objectUrl);
          cleanupVideoCache();
        }

        setVideoData(objectUrl);
        if (onLoadRef.current) {
          onLoadRef.current({ target: { src: objectUrl } });
        }

      } catch {
        if (!mountedRef.current) return;
        setError(true);
        if (onErrorRef.current) {
          onErrorRef.current({ target: { src: null } });
        }
      } finally {
        pendingRequests.delete(src);
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchVideo().catch(() => {
      /* errors handled inside fetchVideo */
    });

    // Fetch poster with authentication if it's a backend URL
    const fetchPoster = async () => {
      if (!poster || posterCache.has(poster) || poster.includes('amazonaws.com') || poster.includes('s3.amazonaws.com')) {
        if (poster && posterCache.has(poster)) {
          setPosterData(posterCache.get(poster));
        }
        return;
      }

      try {
        let token;
        if (isAdminPortal) {
          token = localStorage.getItem('access_token') || Cookies.get('access_token');
        } else {
          token = Cookies.get('merchant_access_token');
        }

        if (!token) return;

        const response = await fetch(poster, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          posterCache.set(poster, objectUrl);
          cleanupPosterCache();
          setPosterData(objectUrl);
        }
      } catch {
        /* poster is optional; keep default poster prop */
      }
    };

    fetchPoster().catch(() => {
      /* poster fetch failures are non-fatal */
    });

    return () => {
      mountedRef.current = false;
      // Do not revoke object URL here as it's stored in the global videoCache/posterCache
    };
  }, [src, poster, isAdminPortal]);

  // Show loading state - keep poster visible for smooth transition
  if (loading && !videoData) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          position: 'relative',
          overflow: 'hidden',
          ...style
        }}
        className={className}
      >
        {poster ? (
          <>
            <SecureImage
              src={poster}
              useThumbnail={true}
              hideLoader={false}  // Show loader if poster not cached
              onLoad={() => setPosterLoaded(true)}  // Track when poster loads
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: style.maxWidth || '85vw',
                maxHeight: style.maxHeight || '85vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
            {/* Only show overlay spinner after poster has loaded */}
            {posterLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.3)',
                  zIndex: 1
                }}
              >
                <CircularProgress size={32} sx={{ color: 'white' }} />
              </Box>
            )}
          </>
        ) : (
          <>
            <CircularProgress size={24} />

          </>
        )}
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // minHeight: 200,
          bgcolor: 'grey.100',
          borderRadius: 1,
          flexDirection: 'column',
          gap: 1,
          ...style
        }}
        className={className}
      >
        <Typography variant="body2" color="error">
          Failed to load video
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Please try again later
        </Typography>
      </Box>
    );
  }

  // Show video with smooth fade-in
  if (videoData) {
    return (
      <video
        key={src}
        {...otherProps}
        src={videoData}
        poster={posterData || poster}
        style={{
          ...style,
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out'
        }}
        className={className}
        controls={controls} // Use the controls prop instead of hardcoded true
        // If React state lags, still reveal the element once the browser can play (fixes "stuck on play")
        onCanPlay={(e) => {
          setLoading(false);
          if (typeof otherProps.onCanPlay === 'function') {
            otherProps.onCanPlay(e);
          }
        }}
        // Firefox often surfaces MEDIA_ERR_ABORTED during heavy 4K blob decode or pipeline resets — not a hard failure
        onError={(e) => {
          const code = e?.target?.error?.code;
          if (code === MediaError.MEDIA_ERR_ABORTED) {
            return;
          }
          setError(true);
          if (typeof otherProps.onError === 'function') {
            otherProps.onError(e);
          }
          if (onErrorRef.current) {
            onErrorRef.current(e);
          }
        }}
      />
    );
  }

  // Fallback
  return null;
};

export default SecureVideo;
